import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Download } from 'lucide-react';
import ActivityRevenueReport, { PaymentTransactionsReport } from '../components/ActivityRevenueReport';
import { getMyVenues } from '../services/venueService';
import { getOwnerReport } from '../services/reportService';

function todayPht() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Manila', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}
function initialDates() {
  const end = todayPht();
  const start = new Date(`${end}T00:00:00Z`); start.setUTCDate(start.getUTCDate() - 29);
  return { start: start.toISOString().slice(0, 10), end };
}
function csvCell(value) {
  const text = String(value ?? '');
  return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}
export default function ActivityReportPage({ mode }) {
  const revenueMode = mode === 'revenue';
  const title = revenueMode ? 'Revenue by Activity' : 'Payment Transactions';
  const [venues, setVenues] = useState([]);
  const [venueId, setVenueId] = useState('');
  const [dates, setDates] = useState(initialDates);
  const [activity, setActivity] = useState('All');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [venuesLoading, setVenuesLoading] = useState(true);
  const [request, setRequest] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    getMyVenues().then(data => {
      if (!active) return;
      const rows = Array.isArray(data) ? data : data?.venues || [];
      setVenues(rows); setVenueId(rows[0]?.id || rows[0]?._id || '');
      setVenuesLoading(false);
    }).catch(() => { if (active) { setError('Unable to load venues.'); setVenuesLoading(false); } });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    setRequest(null); setReport(null); setError(''); setLoading(false);
  }, [mode]);
  useEffect(() => {
    let active = true;
    if (!request || request.mode !== mode) return;
    getOwnerReport(request.venueId, request.start, request.end, null, request.activity)
      .then(data => { if (active) setReport(data); })
      .catch(e => { if (active) { const message = e.response?.data?.message || 'Unable to load report.'; setError(message); toast.error(message); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [request, mode]);
  function clearReport() {
    setRequest(null); setReport(null); setError(''); setLoading(false);
  }
  function generate(selectedDates = dates) {
    clearReport();
    if (!venueId) { setError('Select a venue to view this report.'); return; }
    if (!selectedDates.start || !selectedDates.end || selectedDates.end < selectedDates.start) {
      setError('Choose a valid payment date range.'); return;
    }
    setLoading(true);
    setRequest({ mode, venueId, start: selectedDates.start, end: selectedDates.end, activity });
  }
  function showToday() {
    const today = todayPht();
    const selectedDates = { start: today, end: today };
    setDates(selectedDates); generate(selectedDates);
  }
  function exportCsv() {
    if (!report || loading) return;
    const rows = [[title, report.venue?.name], ['Payment period', `${dates.start} to ${dates.end}`], ['Activity', activity], []];
    if (revenueMode) {
      const fields = ['equipmentRental', 'netVenueEarnings', 'venueDirectCollections', 'onlineEarnings', 'transactions'];
      rows.push(['Activity', 'Equipment rental', 'Net venue earnings', 'Received directly', 'Online earnings', 'Transactions']);
      const total = fields.map(() => 0);
      for (const row of report.activityRevenue || []) { const values = fields.map((key, i) => { const value = Number(row[key] || 0); total[i] += value; return value; }); rows.push([row.label, ...values]); }
      rows.push(['Total', ...total]);
    } else {
      rows.push(['Transaction ID', 'Activity', 'Payment date (PHT)', 'Method', 'Reference', 'Contact name', 'Contact email', 'Collected', 'Venue revenue', 'Group payment', 'Participants / allocations']);
      for (const tx of report.transactions || []) rows.push([tx.transactionId, tx.activityType, tx.paidAt, tx.paymentMethod, tx.paymentReference, tx.contactName, tx.contactEmail, tx.collected, tx.revenue, tx.isGroupPayment ? 'Yes' : 'No', tx.participants.map(p => `${p.name}: entry ${p.entryAmount}, equipment ${p.equipmentRentalAmount}`).join('; ')]);
    }
    const url = URL.createObjectURL(new Blob([`\uFEFF${rows.map(row => row.map(csvCell).join(',')).join('\n')}`], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a'); link.href = url; link.download = `${mode}-${dates.start}-${dates.end}.csv`; link.click(); URL.revokeObjectURL(url);
  }
  return <div className="space-y-5">
    <div className="flex flex-wrap items-center justify-between gap-3"><div><h1 className="text-2xl font-bold text-[#1F2937]">{title}</h1><p className="mt-1 text-sm text-[#64748B]">{revenueMode ? 'Compare court bookings, Open Play and Step Dance earnings.' : 'Review individual and group payments, with participant allocations.'}</p></div><button className="btn-secondary" disabled={!report || loading} onClick={exportCsv}><Download className="mr-2 h-4 w-4" />Export CSV</button></div>
    <section className="card flex flex-wrap items-end gap-4 p-4">
      <label className="text-xs font-medium text-[#64748B]">Venue<select className="input-field mt-1" value={venueId} disabled={loading || venuesLoading} onChange={e => { setVenueId(e.target.value); clearReport(); }}><option value="">Select venue</option>{venues.map(v => <option key={v.id || v._id} value={v.id || v._id}>{v.name}</option>)}</select></label>
      <label className="text-xs font-medium text-[#64748B]">Activity<select className="input-field mt-1" value={activity} disabled={loading} onChange={e => { setActivity(e.target.value); clearReport(); }}><option value="All">All activities</option><option value="CourtBookings">Court Bookings</option><option value="OpenPlay">Open Play</option><option value="StepDance">Step Dance</option></select></label>
      <label className="text-xs font-medium text-[#64748B]">Payment start date<input type="date" className="input-field mt-1" value={dates.start} max={dates.end} disabled={loading} onChange={e => { setDates(d => ({ ...d, start: e.target.value })); clearReport(); }} /></label>
      <label className="text-xs font-medium text-[#64748B]">Payment end date<input type="date" className="input-field mt-1" value={dates.end} min={dates.start} disabled={loading} onChange={e => { setDates(d => ({ ...d, end: e.target.value })); clearReport(); }} /></label>
      <button type="button" className="btn-secondary" disabled={loading || venuesLoading || !venueId} onClick={showToday}>Today</button>
      <button type="button" className="btn-primary" disabled={loading || venuesLoading || !venueId} onClick={() => generate()}>Generate Report</button>
    </section>
    {venuesLoading ? <p role="status" className="card p-8">Loading venues…</p> : loading ? <p role="status" className="card p-8">Loading report…</p> : error ? <p role="alert" className="card p-8 text-red-700">{error}</p> : !venueId ? <p className="card p-8">Select a venue to view this report.</p> : report ? (revenueMode ? <ActivityRevenueReport revenue={report.activityRevenue} /> : <PaymentTransactionsReport transactions={report.transactions} />) : <p className="card p-8 text-sm text-[#64748B]">Choose your filters, then click Generate Report or Today to load the report.</p>}
  </div>;
}
